'use client'

import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import styles from './MoreWorks.module.scss'
import { Page } from '@/types/database'
import { useAdminContext } from '@/hooks/useAdminContext'
import { usePageNavigation } from '@/hooks/usePageNavigation'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'
import { sanitizeBasicHtml } from '@/lib/utils/html-sanitizer'


export interface MoreWorksProps {
  title?: string
  currentWorkSlug?: string
  selectedWorkIds?: string // Comma-separated work IDs for admin override
  showNavigation?: 'auto' | 'selected' // auto = next/prev, selected = admin chosen
}

interface WorkPage extends Page {
  workData: {
    title: string
    subtitle: string
    thumbnail: string
    date: string
    url: string
  }
}



export const MoreWorks: React.FC<MoreWorksProps> = ({
  title = 'More Works',
  currentWorkSlug = '',
  selectedWorkIds = '',
  showNavigation = 'auto'
}) => {
  const [works, setWorks] = useState<WorkPage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const router = useRouter()
  const { isAdminContext } = useAdminContext()
  
  // Animation states
  const [titleInView, setTitleInView] = useState(false)
  const [gridInView, setGridInView] = useState(false)
  
  // Refs for intersection observers
  const titleRef = useRef<HTMLHeadingElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()
  

  const { navigateToPage } = usePageNavigation()
  
  // Check if animations should be enabled
  const canAnimate = !isAdminMode && preloaderState === 'hidden' && !isInitialLoad

  // Title intersection observer
  useEffect(() => {
    const element = titleRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setTitleInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {
          setTitleInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Grid intersection observer
  useEffect(() => {
    const element = gridRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setGridInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {
          setGridInView(true)
        }
      },
      { threshold: 0.2 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])
  
  // Handle work item clicks in admin context
  const handleWorkClick = async (e: React.MouseEvent<HTMLAnchorElement>, work: WorkPage) => {
    if (isAdminContext) {
      e.preventDefault()
      // Redirect to admin editor for this work
      router.push(`/admin/editor/${work.id}`, { scroll: false })
    } else {
      // For front-end navigation, use proper preloading
      const href = work.workData.url
      if (href !== pathname) {
        e.preventDefault()
        await navigateToPage(href, pathname)
      }
    }
  }

  useEffect(() => {
    const fetchWorks = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch works from the public works API endpoint
        const response = await fetch('/api/works')
        if (!response.ok) {
          throw new Error('Failed to fetch works')
        }
        
        const workPages: WorkPage[] = await response.json()

        let filteredWorks: WorkPage[] = []

        if (showNavigation === 'selected' && selectedWorkIds) {
          // Admin has selected specific works to show
          const selectedIds = selectedWorkIds.split(',').map(id => id.trim())
          filteredWorks = workPages.filter(work => selectedIds.includes(work.id))
        } else {
          // Auto mode: show next and previous works
          if (currentWorkSlug) {
            const currentIndex = workPages.findIndex(work => work.slug === currentWorkSlug)
            
            if (currentIndex !== -1 && workPages.length > 1) {
              // Get previous and next works
              const prevWork = workPages[currentIndex - 1]
              const nextWork = workPages[currentIndex + 1]
              
              // Strategy: Always try to show exactly 2 works when possible
              if (workPages.length === 2) {
                // If only 2 works total, show the other one
                filteredWorks = workPages.filter((work, index) => index !== currentIndex)
              } else if (workPages.length > 2) {
                // Prefer adjacent works, but ensure we always have 2
                if (prevWork) filteredWorks.push(prevWork)
                if (nextWork) filteredWorks.push(nextWork)
                
                // If at start/end of list, fill to 2 works
                if (filteredWorks.length < 2) {
                  const otherWorks = workPages.filter((work, index) => 
                    index !== currentIndex && 
                    !filteredWorks.some(fw => fw.id === work.id)
                  )
                  
                  // Add works to reach exactly 2
                  while (filteredWorks.length < 2 && otherWorks.length > 0) {
                    filteredWorks.push(otherWorks.shift()!)
                  }
                }
              } else {
                // Only 1 work total (the current one), show nothing
                filteredWorks = []
              }
            }
          }
        }

        setWorks(filteredWorks)
      } catch (err) {

        setError(err instanceof Error ? err.message : 'Failed to load works')
      } finally {
        setLoading(false)
      }
    }

    fetchWorks()
  }, [currentWorkSlug, selectedWorkIds, showNavigation])

  if (loading) {
    return (
      <section className={styles['more-works']}>
        <div className={styles['more-works__container']}>
          <div className={styles['more-works__loading']}>
            <div className={styles['more-works__spinner']}></div>
            <p>Loading more works...</p>
          </div>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className={styles['more-works']}>
        <div className={styles['more-works__container']}>
          <div className={styles['more-works__error']}>
            <h3>Error loading works</h3>
            <p>{error}</p>
          </div>
        </div>
      </section>
    )
  }

  // Don't render if no works found
  if (works.length === 0) {
    return null
  }

  return (
    <section className={styles['more-works']}>
      <div className={styles['more-works__container']}>
        <motion.h2 
          ref={titleRef}
          className={styles['more-works__title']}
          initial={{ opacity: isAdminMode ? 1 : 0 }}
          animate={{ opacity: titleInView ? 1 : 0 }}
          transition={{
            duration: 0.8,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(title) }}
        />

        <motion.div 
          ref={gridRef}
          className={styles['more-works__grid']}
          initial={{ opacity: isAdminMode ? 1 : 0 }}
          animate={{ opacity: gridInView ? 1 : 0 }}
          transition={{
            duration: 0.8,
            delay: 0.2,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          {works.map((work) => (
            <div
              key={work.id}
              className={styles['more-works__card']}
            >
              <Link 
                href={work.workData.url}
                onClick={(e) => handleWorkClick(e, work)}
                style={{ display: 'block', width: '100%', height: '100%' }}
              >
                <div className={styles['more-works__card-image']}>
                  <Image 
                    src={work.workData.thumbnail} 
                    alt={work.workData.title}
                    className={styles['more-works__card-img']}
                    width={1200}
                    height={675}
                    quality={95}
                    priority={works.indexOf(work) < 2} // Prioritize first 2 images
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 1200px"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  
                  <div className={styles['more-works__card-content']}>
                    <h3 
                      className={styles['more-works__card-title']}
                      dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(work.workData.title) }}
                    />
                    
                    <div className={styles['more-works__card-meta']}>
                      {work.workData.subtitle && (
                        <span className={styles['more-works__card-subtitle']}>
                          {work.workData.subtitle}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}